import { useEffect, useMemo, useState } from "react";
import { HitInlineLoader } from "@/components/loading/HitInlineLoader";
import { rarityTier, TIER_CHIP } from "@/lib/rarityTier";
import { makeCardImgFallback } from "@/lib/imageFallback";

const imgSrc = (u) => !u ? null : u.startsWith("http") ? u
  : u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${u}`;

const PKM_TO_VARIANT = { reverse: "reverse_holo" };

// Convierte la versión elegida en la fila en lo que P3 necesita (variante o impresión Yu-Gi-Oh).
export const versionOpts = (game, v) => {
  if (game === "yugioh") return { printing: { set_code: v.set_code, rarity: v.rarity }, label: v.label };
  if (game === "riftbound") return { variant: v.key || "normal", label: v.label };
  return { variant: PKM_TO_VARIANT[v.key] || "normal", label: v.label };
};

const FilterSelect = ({ label, value, options, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className={`h-8 rounded-full border px-3 text-xs bg-[#0A0A0A] ${value ? "border-[#FF4438] text-white" : "border-[#262626] text-neutral-300"}`}>
    <option value="">{label} ▾</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

// Alto del encabezado fijo → los filtros quedan pegados justo debajo.
const useHeaderHeight = () => {
  const [h, setH] = useState(64);
  useEffect(() => {
    const el = document.querySelector('[data-testid="site-header"]');
    if (!el) { return undefined; }
    const ro = new ResizeObserver(() => setH(el.offsetHeight));
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, []);
  return h;
};

const Pending = () => <span className="inline-block h-3 w-24 rounded bg-white/5 animate-pulse align-middle" />;

const ResultRow = ({ c, best, selected, game, onPick, pending, kbActive, kbVersion }) => {
  const tier = rarityTier(c.rarity);
  const meta = [c.set_name, c.local_id ? `#${c.local_id}` : null].filter(Boolean).join(" · ");
  const sub = [c.year, (c.languages || []).map((l) => l.toUpperCase()).join(" ")].filter(Boolean).join(" · ");
  return (
    <li className={`flex gap-3 p-2.5 rounded-[14px] border transition-colors ${kbActive ? "ring-2 ring-white/70" : ""} ${best ? "border-[#FF4438]" : selected ? "border-[#FF4438]/60 bg-[#FF4438]/5" : "border-[#262626] hover:border-neutral-600"}`}
        data-testid={`publish-result-${c.id}`} data-kb-active={kbActive ? "true" : undefined}
        ref={(el) => { if (kbActive && el) el.scrollIntoView({ block: "nearest" }); }}>
      <button type="button" onClick={() => onPick(c, {})} className="w-14 h-[78px] shrink-0 rounded-md overflow-hidden bg-black grid place-items-center" data-testid={`publish-result-pick-${c.id}`}>
        {imgSrc(c.image_url)
          ? <img src={imgSrc(c.image_url)} alt={c.name} loading="lazy" className="w-full h-full object-contain"
                 onError={makeCardImgFallback((img) => { img.style.display = "none"; })} />
          : <span className="text-[9px] text-neutral-600">Sin imagen</span>}
      </button>
      <div className="flex-1 min-w-0">
        <button type="button" onClick={() => onPick(c, {})} className="text-left w-full">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-white truncate">{c.name}</span>
            {c.rarity && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${TIER_CHIP[tier]}`} data-testid={`publish-result-rarity-${c.id}`}>{c.rarity}</span>}
          </div>
          {meta && <div className="text-xs text-neutral-400 truncate mt-0.5">{meta}</div>}
          {sub ? <div className="text-[11px] font-mono text-neutral-500 mt-0.5" data-testid={`publish-result-meta-${c.id}`}>{sub}</div>
            : pending && <div className="mt-0.5 h-4"><Pending /></div>}
        </button>
        {pending && !c.versions?.length && <div className="mt-1.5 h-7 flex items-center"><Pending /></div>}
        {c.versions?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {c.versions.map((v, vi) => (
              <button key={v.key} type="button" onClick={() => onPick(c, versionOpts(game, v))} data-testid={`publish-result-version-${c.id}-${v.key}`}
                className={`h-7 px-2.5 rounded-full border ${kbActive && kbVersion === vi ? "border-white" : "border-[#333]"} text-[11px] text-neutral-200 hover:border-[#FF4438] hover:text-white transition-colors`}>{v.label}</button>
            ))}
          </div>
        )}
        {c.first_edition_available && (
          <button type="button" onClick={() => onPick(c, { variant: "normal", first_edition: true, label: "1.ª edición" })} data-testid={`publish-result-first-edition-${c.id}`}
            className="mt-1 text-[11px] text-amber-300 underline">¿Es 1.ª edición? Indícalo aquí</button>
        )}
      </div>
    </li>
  );
};

// P2 · Resultados — más coincidencias y más datos; solo lo que trae la fuente.
export const SearchResultsP2 = ({ results, total, hasMore, loadingMore, onMore, onPick, selectedId, game, onNewCard, enriching, kbIndex = -1, kbVersion = 0 }) => {
  const [f, setF] = useState({ set: "", rarity: "", year: "" });
  const top = useHeaderHeight();
  const opts = useMemo(() => {
    const u = (k) => [...new Set(results.map((r) => r[k]).filter(Boolean))].sort();
    return { set: u("set_name"), rarity: u("rarity"), year: u("year").reverse() };
  }, [results]);
  const rows = results.filter((r) => (!f.set || r.set_name === f.set) && (!f.rarity || r.rarity === f.rarity) && (!f.year || r.year === f.year));
  const left = Math.max(0, (total || 0) - results.length);
  return (
    <div className="mt-4 space-y-3" data-testid="publish-results-p2">
      <div className="sticky z-20 -mx-6 px-6 py-2 bg-[#080808] flex flex-wrap items-center gap-2 border-b border-[#171717]" style={{ top }} data-testid="publish-filters">
        <button type="button" onClick={() => setF({ set: "", rarity: "", year: "" })} data-testid="publish-filter-all"
          className={`h-8 px-3 rounded-full border text-xs ${!f.set && !f.rarity && !f.year ? "border-white bg-white text-black font-bold" : "border-[#262626] text-neutral-300"}`}>
          Todos · {total || results.length}
        </button>
        {opts.set.length > 1 && <FilterSelect label="Set" value={f.set} options={opts.set} onChange={(v) => setF({ ...f, set: v })} testid="publish-filter-set" />}
        {opts.rarity.length > 1 && <FilterSelect label="Rareza" value={f.rarity} options={opts.rarity} onChange={(v) => setF({ ...f, rarity: v })} testid="publish-filter-rarity" />}
        {opts.year.length > 1 && <FilterSelect label="Año" value={f.year} options={opts.year} onChange={(v) => setF({ ...f, year: v })} testid="publish-filter-year" />}
      </div>
      <ul className="space-y-2">
        {rows.map((c, i) => <ResultRow key={c.id} c={c} best={i === 0 && !f.set && !f.rarity && !f.year} selected={selectedId === c.id} game={game} onPick={onPick}
          kbActive={results[kbIndex]?.id === c.id} kbVersion={kbVersion}
          pending={enriching && game === "pokemon" && !c.enriched && !c.is_custom} />)}
      </ul>
      {enriching && <HitInlineLoader section="search" />}
      {hasMore && (
        <button type="button" onClick={onMore} disabled={loadingMore} data-testid="publish-search-more"
          className="w-full h-10 rounded-full border border-[#262626] text-sm text-neutral-200 hover:border-neutral-500">
          {loadingMore ? "Cargando…" : `Ver ${left > 0 ? left : ""} más ↓`}
        </button>
      )}
      <div className="flex items-center justify-between gap-3 p-4 rounded-[16px] border border-dashed border-amber-500/50 bg-amber-500/5" data-testid="publish-not-found-card">
        <div>
          <div className="text-sm font-semibold text-white">¿No está tu carta?</div>
          <div className="text-xs text-neutral-400">Súbela igual. Se publica sola al aprobarse.</div>
        </div>
        <button type="button" onClick={onNewCard} data-testid="publish-new-card" className="h-9 px-4 rounded-full bg-amber-500 text-black text-sm font-bold hover:bg-amber-400">Carta nueva</button>
      </div>
    </div>
  );
};
